/**
 * Curated catalogue-item icons — Step 65 (Kai's ruling: lucide picker now,
 * custom upload rides the attachment storage seam later). One list drives
 * the admin picker AND every render site, so an unknown stored name always
 * degrades to the same fallback rather than a blank.
 */
import {
  AtSign,
  Building2,
  Cloud,
  CreditCard,
  Database,
  FileText,
  FolderOpen,
  Globe,
  HardDrive,
  Headphones,
  KeyRound,
  Laptop,
  LayoutGrid,
  LifeBuoy,
  Lock,
  Mail,
  Monitor,
  Network,
  Plane,
  Printer,
  Radio,
  Server,
  ShieldCheck,
  Smartphone,
  UserMinus,
  UserPlus,
  Users,
  Wifi,
  Wrench,
  type LucideIcon,
} from 'lucide-react';

export const CATALOGUE_ICONS: { name: string; Icon: LucideIcon }[] = [
  { name: 'user-plus', Icon: UserPlus },
  { name: 'user-minus', Icon: UserMinus },
  { name: 'users', Icon: Users },
  { name: 'mail', Icon: Mail },
  { name: 'at-sign', Icon: AtSign },
  { name: 'key-round', Icon: KeyRound },
  { name: 'lock', Icon: Lock },
  { name: 'shield-check', Icon: ShieldCheck },
  { name: 'monitor', Icon: Monitor },
  { name: 'laptop', Icon: Laptop },
  { name: 'smartphone', Icon: Smartphone },
  { name: 'printer', Icon: Printer },
  { name: 'headphones', Icon: Headphones },
  { name: 'radio', Icon: Radio },
  { name: 'wifi', Icon: Wifi },
  { name: 'network', Icon: Network },
  { name: 'server', Icon: Server },
  { name: 'cloud', Icon: Cloud },
  { name: 'database', Icon: Database },
  { name: 'hard-drive', Icon: HardDrive },
  { name: 'globe', Icon: Globe },
  { name: 'plane', Icon: Plane },
  { name: 'building', Icon: Building2 },
  { name: 'credit-card', Icon: CreditCard },
  { name: 'file-text', Icon: FileText },
  { name: 'folder-open', Icon: FolderOpen },
  { name: 'wrench', Icon: Wrench },
  { name: 'life-buoy', Icon: LifeBuoy },
];

/** Render a stored icon name; unknown/empty names fall back to the grid. */
/**
 * An item's icon — an uploaded image if it has one, else the curated lucide
 * name, else the fallback.
 *
 * That ORDER is the point (Step 73). The upload does not replace `icon`, it
 * sits in front of it: deleting an uploaded file reveals the name the item
 * always had rather than a hole, and an item that never uploaded anything is
 * completely unaffected.
 *
 * The image is served by `/api/media/:id` like every other binary, so it
 * inherits per-owner authorisation and the `nosniff`/attachment headers
 * rather than this one field growing a second, weaker path to disk.
 */
export function CatalogueIcon({
  name,
  attachmentId,
  className,
}: {
  name: string | null;
  attachmentId?: string | null | undefined;
  className?: string;
}) {
  const size = className ?? 'h-4 w-4';
  if (attachmentId) {
    return (
      <img
        src={`/api/media/${attachmentId}`}
        alt=""
        // Decorative: the item's NAME is always beside it, so announcing the
        // icon would just read the label twice.
        aria-hidden
        className={`${size} rounded object-contain`}
      />
    );
  }
  const Icon = CATALOGUE_ICONS.find((i) => i.name === name)?.Icon ?? LayoutGrid;
  return <Icon className={size} />;
}
